import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IRecorrido, IActivo, IFilter, IUpdateActivo } from 'modelos/src';
import { RecorridosService } from '../../../../../../../../auxiliares/servicios/http/recorridos.service';
import { HelperService } from '../../../../../../../../auxiliares/servicios/helper.service';
import { MapCardConfig } from '../../../../../../../../componentes/map-card/map-card.component';
import { MapCardComponent } from '../../../../../../../../componentes/map-card/map-card.component';
import { MapaFacilData } from '../../../../../../../../auxiliares/mapa-facil/interfaces';
// import { OpenLayersService } from '../../../../../../../../auxiliares/servicios/openLayers.service'; // REMOVED: Migrated to Mapbox
import { DialogService } from '../../../../../../../../auxiliares/dialog/dialog.service';
import { ActivosService } from '../../../../../../../../auxiliares/servicios/http/activos.service';

export interface AsignarRecorridoDialogData {
  vehiculo: IActivo;
}

@Component({
  selector: 'app-asignar-recorrido-dialog',
  templateUrl: './asignar-recorrido-dialog.component.html',
  styleUrls: ['./asignar-recorrido-dialog.component.scss'],
  standalone: false,
})
export class AsignarRecorridoDialogComponent implements OnInit {
  dialogRef =
    inject<MatDialogRef<AsignarRecorridoDialogComponent>>(MatDialogRef);
  data = inject<AsignarRecorridoDialogData>(MAT_DIALOG_DATA);
  private recorridosService = inject(RecorridosService);
  private helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private activosService = inject(ActivosService);

  @ViewChild(MapCardComponent) mapCard?: MapCardComponent;

  public recorridos: IRecorrido[] = [];
  public recorridoSeleccionado?: string;
  public recorridoDetalle?: IRecorrido;

  /** El recorrido elegido mide cumplimiento (real vs deseado). */
  get mideCumplimiento(): boolean {
    return !!this.recorridoDetalle?.cumplimiento?.activo;
  }

  /** Recorrido que el vehículo tiene asignado en este momento. */
  get idRecorridoActual(): string | undefined {
    return this.data.vehiculo?.vehiculo?.idRecorrido;
  }

  get tieneRecorridoAsignado(): boolean {
    return !!this.idRecorridoActual;
  }

  /**
   * Hay algo distinto para asignar. Si el combo está vacío o tiene el mismo
   * recorrido que ya está asignado, no hay nada que confirmar (para desasignar
   * está el botón de quitar).
   */
  get puedeAsignar(): boolean {
    return (
      !!this.recorridoSeleccionado &&
      this.recorridoSeleccionado !== this.idRecorridoActual
    );
  }

  get largoKm(): number {
    return (this.recorridoDetalle?.geometria?.longitudMetros ?? 0) / 1000;
  }

  /**
   * Resumen de lo que va a pasar al asignar el recorrido, con los defaults ya
   * resueltos (el backend aplica los mismos si el campo viene vacío).
   */
  get cumplimiento() {
    const config = this.recorridoDetalle?.cumplimiento;
    const geometria = this.recorridoDetalle?.geometria;
    if (!config) return undefined;
    const tramos = geometria?.cantidadBuckets ?? 0;
    return {
      tramos,
      resolucion: tramos ? 100 / tramos : 0,
      toleranciaMetros: config.toleranciaMetros ?? 40,
      minimoPorcentaje: config.minimoPorcentaje ?? 100,
      inicioEstricto: config.inicioEstricto ?? true,
      porTiempo: !!config.porTiempo,
      duracionEsperada: this.recorridoDetalle?.duracion,
      toleranciaTiempo: config.toleranciaTiempoPorcentaje ?? 20,
      esCiclico: !!config.esCiclico,
      timeoutMinutos: config.timeoutMinutos ?? 30,
    };
  }
  public loading: boolean = false;
  public mapCardConfig?: MapCardConfig;
  public matOptions: MapaFacilData;

  async ngOnInit() {
    this.initializeMapConfig();
    if (this.idRecorridoActual) {
      this.recorridoSeleccionado = this.idRecorridoActual;
      await this.onRecorridoChange();
    }
    await this.cargarRecorridos();
  }

  private initializeMapConfig() {
    // Inicializar mapa vacío
    const mapaFacil: MapaFacilData = {
      zoom: 6,
      center: [-58.3816, -34.6037], // Buenos Aires por defecto
    };

    this.matOptions = mapaFacil;

    this.mapCardConfig = {
      title: 'Vista Previa del Recorrido',
      subtitle: 'Seleccioná un recorrido para visualizarlo',
      mapOptions: mapaFacil,
      color: 'blue',
      height: '500px',
      nombreMap: 'mapa-recorrido-preview',
    };
  }

  private async cargarRecorridos() {
    try {
      this.loading = true;
      const filter: IFilter<IRecorrido> = {
        categoria: 'Vehiculo',
      };
      const response = await this.recorridosService.getFiltered({
        filter: JSON.stringify(filter),
        includeChildren: true,
      });
      this.recorridos = response.datos;
    } catch (error) {
      console.error('Error al cargar recorridos:', error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  public async onRecorridoChange() {
    if (!this.recorridoSeleccionado) {
      this.recorridoDetalle = undefined;
      // Resetear mapa a vista inicial
      this.matOptions = {
        zoom: 6,
        center: [-58.3816, -34.6037],
      };
      return;
    }

    try {
      this.loading = true;
      this.recorridoDetalle = await this.recorridosService.getById(
        this.recorridoSeleccionado,
      );
      const coordinates = this.recorridoDetalle.geojson.coordinates;

      // Las coordenadas ya están en formato lng/lat para Mapbox
      const points = coordinates as [number, number][];

      // IMPORTANTE: Crear un NUEVO objeto para que Angular detecte el cambio
      this.matOptions = {
        polylines: [
          {
            points: points,
            color: this.recorridoDetalle.color || '#2196f3',
            width: 3,
            centrar: true,
          },
        ],
      };
    } catch (error) {
      console.error('Error al cargar detalle del recorrido:', error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  // private prepareMapCardConfig() {
  //   if (!this.recorridoDetalle?.geojson?.coordinates) {
  //     return;
  //   }

  //   const mapaFacil: MapaFacilData;

  //   this.mapCardConfig = {
  //     title: 'Vista Previa del Recorrido',
  //     subtitle: this.recorridoDetalle.nombre,
  //     mapOptions: mapaFacil,
  //     color: 'blue',
  //     height: '500px',
  //     nombreMap: 'mapa-recorrido-preview',
  //   };
  // }

  public async onConfirmRecorrido(): Promise<void> {
    if (!this.recorridoSeleccionado || !this.recorridoDetalle) {
      return;
    }
    const confirmed = await this.dialogService.confirm(
      'Confirmar Asignación',
      `¿Querés comenzar el seguimiento del recorrido "${this.recorridoDetalle.nombre}" para el vehículo ${this.data.vehiculo.identificacion}?`,
      'Confirmar',
      'Cancelar',
    );

    if (confirmed) {
      try {
        // Aquí iría la lógica para asignar el recorrido al vehículo
        // Por ahora solo mostramos un mensaje de éxito

        const updateActivo: IUpdateActivo = {
          vehiculo: { idRecorrido: this.recorridoSeleccionado },
        };
        await this.activosService.update(this.data.vehiculo._id!, updateActivo);
        this.helper.notifSuccess(
          `Recorrido "${this.recorridoDetalle.nombre}" asignado correctamente`,
        );
      } catch (error) {
        console.error('Error al asignar recorrido:', error);
        this.helper.notifError(error);
      }
      // Cerrar y devolver el recorrido seleccionado
      this.dialogRef.close();
    }
  }
  /**
   * Deja al vehículo sin recorrido asignado. Si había una medición de
   * cumplimiento en curso, el backend la cierra al llegar el próximo reporte.
   */
  public async onCancelRecorrido(): Promise<void> {
    const nombre = this.recorridos.find(
      (r) => r._id === this.idRecorridoActual,
    )?.nombre;
    const confirmed = await this.dialogService.confirm(
      'Quitar recorrido',
      `¿Querés quitarle el recorrido ${nombre ? `"${nombre}" ` : ''}a ${
        this.data.vehiculo.identificacion
      }? Se corta el seguimiento y deja de medirse el cumplimiento.`,
      'Quitar',
      'Cancelar',
    );
    if (!confirmed) return;

    try {
      // `null` y no `undefined`: undefined no llega a Mongo y el recorrido
      // quedaría asignado.
      const updateActivo: IUpdateActivo = {
        vehiculo: { idRecorrido: null },
      };
      await this.activosService.update(this.data.vehiculo._id!, updateActivo);
      this.helper.notifSuccess('Recorrido quitado');
    } catch (error) {
      console.error('Error al quitar el recorrido:', error);
      this.helper.notifError(error);
    }
    this.dialogRef.close();
  }
}
